/**
 * TikTok Pixel + Events API helper — mirrors the Meta Pixel helper.
 *
 * Loads `/api/tiktok/config` once, injects TikTok's pixel script if configured,
 * and exposes track* functions that fire BOTH the browser pixel and the
 * server-side Events API in parallel (deduplicated via shared event_id).
 *
 * All helpers no-op silently when TikTok is not configured or when the
 * user has not granted marketing-cookie consent.
 */
import api from "./api";

const CONSENT_KEY = "opik_cookie_consent_v1";
const _hasMarketingConsent = () => {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(CONSENT_KEY) : null;
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed?.marketing;
  } catch (e) { return false; }
};

let _state = { pixelId: null, injected: false, ready: false, loading: null };
const SCRIPT_ID = "tiktok-pixel-loader";

const _inject = (pixelId) => {
  if (_state.injected || typeof window === "undefined" || typeof document === "undefined") return;
  /* eslint-disable */
  !function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = (w[t] = w[t] || []);
    ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
    ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) { var e = ttq._i[t] || []; for (var n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]); return e };
    ttq.load = function (e, n) {
      var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
      var o = n && n.partner;
      ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r; ttq._t = ttq._t || {}; ttq._t[e] = +new Date; ttq._o = ttq._o || {}; ttq._o[e] = n || {};
      var s = d.createElement("script"); s.type = "text/javascript"; s.id = SCRIPT_ID; s.async = !0; s.src = r + "?sdkid=" + e + "&lib=" + t;
      var a = d.getElementsByTagName("script")[0]; a.parentNode.insertBefore(s, a);
    };
    ttq.load(pixelId);
    ttq.page();
  }(window, document, "ttq");
  /* eslint-enable */
  _state.injected = true;
  _state.ready = true;
};

export const initTikTokPixel = async () => {
  if (_state.ready) return _state.pixelId;
  if (_state.loading) return _state.loading;
  _state.loading = api.get("/tiktok/config")
    .then(({ data }) => {
      if (data?.enabled && data?.pixel_id) {
        _state.pixelId = data.pixel_id;
        _inject(data.pixel_id);
      }
      return _state.pixelId;
    })
    .catch(() => null)
    .finally(() => { _state.loading = null; });
  return _state.loading;
};

const _genId = (name) => `${name}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const _cookie = (name) => {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
};
const _urlParam = (name) => {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch (e) { return null; }
};

const _send = (event, properties = {}, capi = {}) => {
  if (!_hasMarketingConsent()) return;
  const eventId = _genId(event);
  // Browser pixel
  try {
    if (typeof window !== "undefined" && window.ttq && _state.ready) {
      window.ttq.track(event, properties, { event_id: eventId });
    }
  } catch (e) { /* no-op */ }
  // Server-side mirror
  try {
    api.post("/tiktok/events", {
      event,
      event_id: eventId,
      event_source_url: typeof window !== "undefined" ? window.location.href : null,
      user_data: {
        ttclid: _urlParam("ttclid"),
        ttp: _cookie("_ttp"),
        client_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        ...capi.user_data,
      },
      properties: { currency: properties.currency || "USD", ...properties },
    }).catch(() => {});
  } catch (e) { /* no-op */ }
};

export const trackTTPageView = () => {
  if (!_state.ready || !_hasMarketingConsent()) return;
  try { window.ttq.page(); } catch (e) { /* no-op */ }
};

export const trackTTViewContent = (gem, valueUsd) => {
  if (!gem) return;
  _send("ViewContent", {
    content_type: "product",
    content_id: gem.id,
    content_name: gem.name,
    content_category: gem.category || "Gemstone",
    value: Number(valueUsd ?? gem.reserve_price ?? 0),
    currency: "USD",
    quantity: 1,
    contents: [{ content_id: gem.id, content_name: gem.name, quantity: 1, price: Number(valueUsd ?? gem.reserve_price ?? 0) }],
  });
};

export const trackTTAddToCart = (gem, valueUsd) => {
  if (!gem) return;
  _send("AddToCart", {
    content_type: "product",
    content_id: gem.id,
    content_name: gem.name,
    value: Number(valueUsd ?? gem.reserve_price ?? 0),
    currency: "USD",
    quantity: 1,
    contents: [{ content_id: gem.id, content_name: gem.name, quantity: 1, price: Number(valueUsd ?? gem.reserve_price ?? 0) }],
  });
};

export const trackTTInitiateCheckout = (gem, valueUsd, method) => {
  if (!gem) return;
  _send("InitiateCheckout", {
    content_type: "product",
    content_id: gem.id,
    content_name: gem.name,
    value: Number(valueUsd ?? gem.reserve_price ?? 0),
    currency: "USD",
    quantity: 1,
    contents: [{ content_id: gem.id, content_name: gem.name, quantity: 1, price: Number(valueUsd ?? gem.reserve_price ?? 0) }],
    description: `Checkout via ${method || "unknown"}`,
  });
};

export const trackTTPurchase = ({ gemId, gemName, value, currency = "USD", email, phone, orderId }) => {
  _send("PlaceAnOrder", {
    content_type: "product",
    content_id: gemId,
    content_name: gemName,
    value: Number(value || 0),
    currency,
    quantity: 1,
    contents: gemId ? [{ content_id: gemId, content_name: gemName, quantity: 1, price: Number(value || 0) }] : [],
    description: orderId,
  }, { user_data: { email, phone, external_id: orderId } });
  // Also fire CompletePayment (TikTok treats these as distinct funnel signals)
  _send("CompletePayment", {
    content_type: "product",
    content_id: gemId,
    content_name: gemName,
    value: Number(value || 0),
    currency,
    quantity: 1,
    contents: gemId ? [{ content_id: gemId, content_name: gemName, quantity: 1, price: Number(value || 0) }] : [],
    description: orderId,
  }, { user_data: { email, phone, external_id: orderId } });
};
